import { Link, useMatches, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/shadcn/breadcrumb'
import { Fragment } from 'react'
import { Home } from 'lucide-react'

interface BreadcrumbHandle {
  breadcrumb: {
    // Static route: translation key + namespace (reactive on language change).
    labelKey?: string
    ns?: string
    // Dynamic crumbs (employee name, folder path) come as a ready-made string
    // via location.state — that's data, not translatable UI text.
    label?: string
    path?: string
  }
}

function isBreadcrumbHandle(handle: unknown): handle is BreadcrumbHandle {
  if (typeof handle !== 'object' || handle === null || !('breadcrumb' in handle)) return false
  const b = (handle as BreadcrumbHandle).breadcrumb
  return typeof b?.label === 'string' || typeof b?.labelKey === 'string'
}

export function AppBreadcrumb() {
  const { t } = useTranslation()
  const matches = useMatches()
  const location = useLocation()
  const stateCrumbs: BreadcrumbHandle['breadcrumb'][] = (location.state as any)?.breadcrumbs ?? []

  const routeCrumbs = matches
    .filter((m) => isBreadcrumbHandle(m.handle))
    .map((m) => (m.handle as BreadcrumbHandle).breadcrumb)

  const crumbs = [...routeCrumbs, ...stateCrumbs]

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          {crumbs.length === 0 ? (
            <BreadcrumbPage><Home size={18}/></BreadcrumbPage>
          ) : (
            <BreadcrumbLink asChild>
              <Link to="/"><Home size={18}/></Link>
            </BreadcrumbLink>
          )}
        </BreadcrumbItem>
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1
          const text = crumb.labelKey ? t(crumb.labelKey, { ns: crumb.ns }) : crumb.label
          return (
            <Fragment key={crumb.path ?? text}>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                {isLast || !crumb.path ? (
                  <BreadcrumbPage>{text}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.path}>{text}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </Fragment>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}
